import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import {
  LayoutDashboard,
  Users,
  CreditCard,
  FileText,
  AlertTriangle,
  Settings,
  LogOut,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const navItems = [
  { to: "/", label: "Overview", icon: LayoutDashboard },
  { to: "/dealers", label: "Dealers", icon: Users },
  { to: "/payments", label: "Payments Received", icon: CreditCard },
  { to: "/invoices", label: "Invoices", icon: FileText },
  { to: "/alerts", label: "Alerts", icon: AlertTriangle },
  { to: "/settings", label: "Settings", icon: Settings },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside className="w-[260px] bg-card border-r flex flex-col shrink-0" data-testid="sidebar">
        <div className="p-5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Zap className="w-4 h-4 text-primary-foreground" />
            </div>
            <div>
              <h1 className="font-display text-base font-semibold tracking-tight">DealerBill</h1>
              <p className="text-[10px] text-muted-foreground tracking-wide uppercase">Billing Middleware</p>
            </div>
          </div>
        </div>

        <Separator />

        <nav className="flex-1 px-3 py-4 space-y-1" data-testid="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              item.to === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={`sidebar-nav-item ${isActive ? "active" : "text-muted-foreground"}`}
                data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <Separator />

        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={logout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-[1400px] px-4 sm:px-6 lg:px-8 py-6">
          {children}
        </div>
      </main>
    </div>
  );
}
